/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-08
 */

import React from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import {
  Business as OverviewIcon,
  People as MembersIcon,
  Send as InvitationsIcon,
  HowToReg as JoinRequestsIcon,
  Settings as SettingsIcon,
  SwapHoriz as SwitchStudioIcon,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import NavigationItem from '../../common/NavigationItem';
import { Studio, StudioPermissions } from '../../../../types/studio';

/**
 * Studio tab navigation component that provides horizontal navigation
 * similar to the main app navigation style.
 */
interface StudioTabNavigationProps {
  /** Studio data */
  studio: Studio;
  /** User's permissions in the studio */
  permissions?: StudioPermissions | null;
  /** Whether to show the navigation (for responsive design) */
  show?: boolean;
  /** Additional CSS class name */
  className?: string;
}

const StudioTabNavigation: React.FC<StudioTabNavigationProps> = ({
  studio,
  permissions,
  show = true,
  className,
}) => {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (!show || !studio) {
    return null;
  }

  // Define navigation items with permissions
  const navigationItems = [
    {
      label: 'Overview',
      path: `/studio/${studio.id}`,
      icon: <OverviewIcon fontSize="small" />,
      show: true, // Always show overview
    },
    {
      label: 'Members',
      path: `/studio/${studio.id}/members`,
      icon: <MembersIcon fontSize="small" />,
      show: permissions?.canViewStudio ?? true, // All roles can view members
    },
    {
      label: 'Invitations',
      path: `/studio/${studio.id}/invitations`,
      icon: <InvitationsIcon fontSize="small" />,
      show: permissions?.canInviteMembers ?? false, // Only Owner/Manager/Admin
    },
    {
      label: 'Join Requests',
      path: `/studio/${studio.id}/join-requests`,
      icon: <JoinRequestsIcon fontSize="small" />,
      show: permissions?.canInviteMembers ?? false, // Only Owner/Manager/Admin can manage join requests
    },
    {
      label: 'Settings',
      path: `/studio/${studio.id}/settings`,
      icon: <SettingsIcon fontSize="small" />,
      show: permissions?.canEditStudio ?? false, // Only Owner/Manager can access settings
    },
    {
      label: 'Switch Studio',
      path: '/studios',
      icon: <SwitchStudioIcon fontSize="small" />,
      show: true, // Always show switch studio
    },
  ];

  // Filter items based on permissions
  const visibleItems = navigationItems.filter(item => item.show);

  // Determine active state for each item
  const getIsActive = (itemPath: string) => {
    if (itemPath === `/studio/${studio.id}`) {
      // Overview is active when on exact studio path or studio path with no additional segments
      return (
        location.pathname === itemPath ||
        (location.pathname.startsWith(`/studio/${studio.id}`) &&
          location.pathname.split('/').length === 3)
      );
    }
    return location.pathname.startsWith(itemPath);
  };

  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? 0.5 : 1,
        px: { xs: 1, sm: 2 },
        py: 1,
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        overflowX: 'auto',
        '&::-webkit-scrollbar': {
          height: 4,
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.divider,
          borderRadius: 2,
        },
      }}
    >
      {visibleItems.map(item => (
        <NavigationItem
          key={item.path}
          label={item.label}
          path={item.path}
          icon={item.icon}
          isActive={getIsActive(item.path)}
        />
      ))}
    </Box>
  );
};

export default StudioTabNavigation;
